import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Building2, Users2, Plus, Pencil, Trash2, Save, X, Percent, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyEntity = {
  code: '', name: '', legal_name: '',
  registration_number: '', tax_number: '', vat_number: '', fsp_number: '',
  address_line1: '', address_line2: '', city: '', province: '', postal_code: '', country: 'South Africa',
  phone: '', email: '', website: '', notes: '',
  is_active: true,
};

const emptyType = {
  code: '', name: '', description: '',
  default_company_entity_id: '', default_welcome_template_id: '',
  reference_id_prefix: '',
  is_active: true, sort_order: 100,
};

const NONE = '__none__';

export default function AdminCompanySettingsPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState('entities');
  const [entities, setEntities] = useState([]);
  const [types, setTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [entityForm, setEntityForm] = useState(null);
  const [typeForm, setTypeForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rateConfig, setRateConfig] = useState(null);
  const [savingRates, setSavingRates] = useState(false);
  const [syncingSarb, setSyncingSarb] = useState(false);

  const load = useCallback(async () => {
    try {
      const [e, t, tpl, rc] = await Promise.all([
        axios.get(`${API_URL}/api/admin/company-entities?include_inactive=true`, { headers }),
        axios.get(`${API_URL}/api/admin/investor-types?include_inactive=true`, { headers }),
        axios.get(`${API_URL}/api/admin/templates`, { headers }).catch(() => ({ data: { templates: [] } })),
        axios.get(`${API_URL}/api/admin/investor-rate-config`, { headers }).catch(() => ({ data: null })),
      ]);
      setEntities(e.data.entities || []);
      setTypes(t.data.types || []);
      const rows = Array.isArray(tpl.data) ? tpl.data : (tpl.data.templates || []);
      setTemplates(rows.filter(x => (x.template_type || '').startsWith('welcome_')));
      if (rc.data) setRateConfig(rc.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load settings');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const pctField = (key) => ({
    value: rateConfig ? (rateConfig[key] * 100).toString() : '',
    onChange: (e) => setRateConfig({ ...rateConfig, [key]: (parseFloat(e.target.value) || 0) / 100 }),
  });

  const saveRateConfig = async () => {
    if (!rateConfig) return;
    setSavingRates(true);
    try {
      const { id, created_at, updated_at, ...body } = rateConfig;
      const r = await axios.put(`${API_URL}/api/admin/investor-rate-config`, body, { headers });
      setRateConfig(r.data);
      toast.success('Investor rate configuration saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSavingRates(false); }
  };

  const syncSarbRates = async () => {
    setSyncingSarb(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/investor-rate-config/sync-sarb`, {}, { headers });
      setRateConfig(r.data);
      toast.success(`Synced from SARB: Repo ${(r.data.sa_repo_rate * 100).toFixed(2)}%, Prime ${(r.data.sa_prime_rate * 100).toFixed(2)}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sync from SARB');
    } finally { setSyncingSarb(false); }
  };

  const saveEntity = async () => {
    if (!entityForm.code || !entityForm.name) { toast.error('Code and Name are required'); return; }
    setSaving(true);
    try {
      const body = { ...entityForm }; delete body._new;
      if (entityForm._new) {
        await axios.post(`${API_URL}/api/admin/company-entities`, body, { headers });
      } else {
        await axios.put(`${API_URL}/api/admin/company-entities/${entityForm.id}`, body, { headers });
      }
      toast.success('Company entity saved');
      setEntityForm(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const deleteEntity = async (row) => {
    if (!window.confirm(`Delete ${row.name}? Investor Types linked to this entity must be unlinked first.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/company-entities/${row.id}`, { headers });
      toast.success('Deleted'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const saveType = async () => {
    if (!typeForm.code || !typeForm.name) { toast.error('Code and Name are required'); return; }
    setSaving(true);
    try {
      const body = { ...typeForm }; delete body._new;
      delete body.default_company_entity; delete body.default_welcome_template;
      if (body.default_company_entity_id === NONE) body.default_company_entity_id = null;
      if (body.default_welcome_template_id === NONE) body.default_welcome_template_id = null;
      ['description', 'reference_id_prefix'].forEach(k => { if (body[k] === '') body[k] = null; });
      if (typeForm._new) {
        await axios.post(`${API_URL}/api/admin/investor-types`, body, { headers });
      } else {
        await axios.put(`${API_URL}/api/admin/investor-types/${typeForm.id}`, body, { headers });
      }
      toast.success('Investor type saved'); setTypeForm(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const deleteType = async (row) => {
    if (!window.confirm(`Delete investor type "${row.code}"? Users still tagged with this type will block the delete.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/investor-types/${row.id}`, { headers });
      toast.success('Deleted'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-company-settings-page">
      <div>
        <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">Settings</h1>
        <p className="text-[#6C757D] mt-1">Company entities, investor types, and workflow defaults.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="entities" data-testid="tab-entities">
            <Building2 className="h-4 w-4 mr-2" /> Company Entities ({entities.length})
          </TabsTrigger>
          <TabsTrigger value="types" data-testid="tab-types">
            <Users2 className="h-4 w-4 mr-2" /> Investor Types ({types.length})
          </TabsTrigger>
          <TabsTrigger value="rates" data-testid="tab-rates">
            <Percent className="h-4 w-4 mr-2" /> Investor Rates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="entities">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Company Entities</CardTitle>
                <CardDescription>
                  Legal companies you operate under. Data here feeds document templates via
                  <code className="ml-1">{'{{company_*}}'}</code> placeholders.
                </CardDescription>
              </div>
              <Button onClick={() => setEntityForm({ ...emptyEntity, _new: true })}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="add-company-entity-btn">
                <Plus className="h-4 w-4 mr-2" /> Add entity
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Legal name</TableHead>
                  <TableHead>Reg no.</TableHead><TableHead>VAT / FSP</TableHead><TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead><TableHead className="w-24"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {entities.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-[#6C757D] py-8">No entities yet.</TableCell></TableRow>
                  ) : entities.map(e => (
                    <TableRow key={e.id} data-testid={`entity-row-${e.code}`}>
                      <TableCell className="font-mono text-xs font-semibold">{e.code}</TableCell>
                      <TableCell className="font-medium">{e.name}</TableCell>
                      <TableCell className="text-sm text-[#6C757D]">{e.legal_name || '—'}</TableCell>
                      <TableCell className="text-xs font-mono">{e.registration_number || '—'}</TableCell>
                      <TableCell className="text-xs">
                        {e.vat_number && <div>VAT: {e.vat_number}</div>}
                        {e.fsp_number && <div>FSP: {e.fsp_number}</div>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {e.email && <div>{e.email}</div>}
                        {e.phone && <div>{e.phone}</div>}
                      </TableCell>
                      <TableCell>{e.is_active !== false ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEntityForm({ ...e })} data-testid={`edit-entity-${e.code}`}><Pencil className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteEntity(e)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="types">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Investor Types</CardTitle>
                <CardDescription>Extensible list — PTC, PTAM, PTE, or any new brand you launch. Each type links to a default Company Entity and a default welcome-email template.</CardDescription>
              </div>
              <Button onClick={() => setTypeForm({ ...emptyType, _new: true })}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="add-investor-type-btn">
                <Plus className="h-4 w-4 mr-2" /> Add type
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Code</TableHead><TableHead>Name</TableHead>
                  <TableHead>Default entity</TableHead><TableHead>Welcome template</TableHead>
                  <TableHead>Ref prefix</TableHead><TableHead>Status</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {types.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-[#6C757D] py-8">No investor types yet.</TableCell></TableRow>
                  ) : types.map(t => (
                    <TableRow key={t.id} data-testid={`type-row-${t.code}`}>
                      <TableCell className="font-mono text-xs font-semibold">{t.code}</TableCell>
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell className="text-sm">{t.default_company_entity?.name || <span className="text-[#6C757D]">— none —</span>}</TableCell>
                      <TableCell className="text-sm">{t.default_welcome_template?.name || <span className="text-[#6C757D]">— auto —</span>}</TableCell>
                      <TableCell className="text-xs font-mono">{t.reference_id_prefix || t.code}</TableCell>
                      <TableCell>{t.is_active !== false ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setTypeForm({ ...t })} data-testid={`edit-type-${t.code}`}><Pencil className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteType(t)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rates">
          <Card>
            <CardHeader>
              <CardTitle>Investor Rate Calculation</CardTitle>
              <CardDescription>
                Controls the New Deal form's automatic investor yield. All PTAM investors, and PTC investors
                01-00/02-00/05-00/06-00/07-00/10-00/11-00/12-00/13-00/14-00/17-00, receive the calculated rate below.
                PTC investors coded 03-* and 09-* (plus 04-00/08-00/15-00/20-00) receive their own fixed rates instead.
                An admin can always override the computed rate manually when recording a deal.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!rateConfig ? (
                <p className="text-sm text-[#6C757D]">Loading…</p>
              ) : (
                <>
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Calculated rate — provision schedule (PTAM / calculated-group PTC)</h4>
                    <p className="text-xs text-[#6C757D] mb-3">Investor rate = borrower interest rate − provision, where the provision grows in two tiers as the borrower rate rises.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div><Label>Tier 1 threshold (%)</Label><Input type="number" step="0.01" {...pctField('tier1_threshold')} data-testid="rate-tier1-threshold" /></div>
                      <div><Label>Tier 1 base provision (%)</Label><Input type="number" step="0.01" {...pctField('tier1_provision')} data-testid="rate-tier1-provision" /></div>
                      <div><Label>Tier 1 provision increase (%)</Label><Input type="number" step="0.01" {...pctField('tier1_increase')} data-testid="rate-tier1-increase" /></div>
                      <div><Label>Tier 2 threshold (%)</Label><Input type="number" step="0.01" {...pctField('tier2_threshold')} data-testid="rate-tier2-threshold" /></div>
                      <div><Label>Tier 2 provision increase (%)</Label><Input type="number" step="0.01" {...pctField('tier2_increase')} data-testid="rate-tier2-increase" /></div>
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-sm mb-2">Fixed rates (PTC carve-outs)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div><Label>PTC 03-* fixed rate (%)</Label><Input type="number" step="0.01" {...pctField('ptc_fixed_rate_03')} data-testid="rate-fixed-03" /></div>
                      <div><Label>PTC 09-* fixed rate (%)</Label><Input type="number" step="0.01" {...pctField('ptc_fixed_rate_09')} data-testid="rate-fixed-09" /></div>
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm">SARB reference rates</h4>
                      <Button variant="outline" size="sm" onClick={syncSarbRates} disabled={syncingSarb} data-testid="sync-sarb-btn">
                        <RefreshCw className={`h-4 w-4 mr-2 ${syncingSarb ? 'animate-spin' : ''}`} />{syncingSarb ? 'Syncing…' : 'Sync from SARB'}
                      </Button>
                    </div>
                    <p className="text-xs text-[#6C757D] mb-3">
                      Linked to the South African Reserve Bank's own published rates (resbank.co.za) — synced automatically every day at 06:00 SAST,
                      or on demand with the button above. Changing either rate here ONLY affects the auto-fill default shown when a NEW deal is
                      created — it can never retroactively change an already-recorded loan's own interest rate, since every loan stores its own
                      rate permanently at the moment it's created.
                      {rateConfig.rates_synced_at && (
                        <> Last synced {new Date(rateConfig.rates_synced_at).toLocaleString()} from {rateConfig.rates_source || 'SARB'}
                        {rateConfig.rates_effective_date ? ` (effective ${rateConfig.rates_effective_date})` : ''}.</>
                      )}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label>SA Repo Rate (%)</Label>
                        <Input type="number" step="0.01" {...pctField('sa_repo_rate')} data-testid="rate-sa-repo" />
                        <p className="text-xs text-[#6C757D] mt-1">Used as the default performance hurdle across all investor profiles.</p>
                      </div>
                      <div>
                        <Label>SA Prime Lending Rate (%)</Label>
                        <Input type="number" step="0.01" {...pctField('sa_prime_rate')} data-testid="rate-sa-prime" />
                        <p className="text-xs text-[#6C757D] mt-1">Used for clients whose borrowing rate is set to Prime + a premium (rate_type = prime_linked on their profile).</p>
                      </div>
                    </div>
                  </div>
                  <Button onClick={saveRateConfig} disabled={savingRates} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-rate-config-btn">
                    <Save className="h-4 w-4 mr-2" />{savingRates ? 'Saving…' : 'Save rate configuration'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Entity Edit Dialog */}
      <Dialog open={!!entityForm} onOpenChange={(v) => !v && setEntityForm(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {entityForm && <>
            <DialogHeader>
              <DialogTitle>{entityForm._new ? 'Add company entity' : `Edit ${entityForm.name || entityForm.code}`}</DialogTitle>
              <DialogDescription>Details here populate <code>{'{{company_*}}'}</code> placeholders on statements, tax certs and welcome letters.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
              <div><Label>Code *</Label><Input value={entityForm.code} onChange={e => setEntityForm({...entityForm, code: e.target.value.toUpperCase()})} placeholder="PTC" data-testid="entity-code" /></div>
              <div><Label>Trading name *</Label><Input value={entityForm.name} onChange={e => setEntityForm({...entityForm, name: e.target.value})} placeholder="Plane Tree Capital" data-testid="entity-name" /></div>
              <div className="md:col-span-2"><Label>Legal name</Label><Input value={entityForm.legal_name || ''} onChange={e => setEntityForm({...entityForm, legal_name: e.target.value})} placeholder="Plane Tree Capital (Pty) Ltd" /></div>
              <div><Label>Registration no.</Label><Input value={entityForm.registration_number || ''} onChange={e => setEntityForm({...entityForm, registration_number: e.target.value})} /></div>
              <div><Label>Tax no.</Label><Input value={entityForm.tax_number || ''} onChange={e => setEntityForm({...entityForm, tax_number: e.target.value})} /></div>
              <div><Label>VAT no.</Label><Input value={entityForm.vat_number || ''} onChange={e => setEntityForm({...entityForm, vat_number: e.target.value})} /></div>
              <div><Label>FSP no.</Label><Input value={entityForm.fsp_number || ''} onChange={e => setEntityForm({...entityForm, fsp_number: e.target.value})} /></div>
              <div className="md:col-span-2"><Label>Address line 1</Label><Input value={entityForm.address_line1 || ''} onChange={e => setEntityForm({...entityForm, address_line1: e.target.value})} /></div>
              <div className="md:col-span-2"><Label>Address line 2</Label><Input value={entityForm.address_line2 || ''} onChange={e => setEntityForm({...entityForm, address_line2: e.target.value})} /></div>
              <div><Label>City</Label><Input value={entityForm.city || ''} onChange={e => setEntityForm({...entityForm, city: e.target.value})} /></div>
              <div><Label>Province</Label><Input value={entityForm.province || ''} onChange={e => setEntityForm({...entityForm, province: e.target.value})} /></div>
              <div><Label>Postal code</Label><Input value={entityForm.postal_code || ''} onChange={e => setEntityForm({...entityForm, postal_code: e.target.value})} /></div>
              <div><Label>Country</Label><Input value={entityForm.country || ''} onChange={e => setEntityForm({...entityForm, country: e.target.value})} /></div>
              <div><Label>Phone</Label><Input value={entityForm.phone || ''} onChange={e => setEntityForm({...entityForm, phone: e.target.value})} /></div>
              <div><Label>Email</Label><Input type="email" value={entityForm.email || ''} onChange={e => setEntityForm({...entityForm, email: e.target.value})} /></div>
              <div className="md:col-span-2"><Label>Website</Label><Input value={entityForm.website || ''} onChange={e => setEntityForm({...entityForm, website: e.target.value})} /></div>
              <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={entityForm.notes || ''} onChange={e => setEntityForm({...entityForm, notes: e.target.value})} /></div>
              <div className="md:col-span-2 flex items-center gap-2">
                <input id="ent_active" type="checkbox" checked={entityForm.is_active !== false} onChange={e => setEntityForm({...entityForm, is_active: e.target.checked})} />
                <Label htmlFor="ent_active" className="cursor-pointer">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEntityForm(null)} disabled={saving}><X className="h-4 w-4 mr-2" />Cancel</Button>
              <Button onClick={saveEntity} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-entity-btn">
                <Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save entity'}
              </Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* Type Edit Dialog */}
      <Dialog open={!!typeForm} onOpenChange={(v) => !v && setTypeForm(null)}>
        <DialogContent className="max-w-xl">
          {typeForm && <>
            <DialogHeader>
              <DialogTitle>{typeForm._new ? 'Add investor type' : `Edit ${typeForm.name}`}</DialogTitle>
              <DialogDescription>Drives the default entity used in merge fields and which welcome template gets sent.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
              <div><Label>Code *</Label><Input value={typeForm.code} onChange={e => setTypeForm({...typeForm, code: e.target.value.toUpperCase()})} placeholder="PTC" data-testid="type-code" /></div>
              <div><Label>Name *</Label><Input value={typeForm.name} onChange={e => setTypeForm({...typeForm, name: e.target.value})} placeholder="Plane Tree Capital" data-testid="type-name" /></div>
              <div className="md:col-span-2"><Label>Description</Label><Textarea rows={2} value={typeForm.description || ''} onChange={e => setTypeForm({...typeForm, description: e.target.value})} /></div>
              <div className="md:col-span-2">
                <Label>Default company entity</Label>
                <Select value={typeForm.default_company_entity_id || NONE} onValueChange={v => setTypeForm({...typeForm, default_company_entity_id: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— none —</SelectItem>
                    {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Default welcome template</Label>
                <Select value={typeForm.default_welcome_template_id || NONE} onValueChange={v => setTypeForm({...typeForm, default_welcome_template_id: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— auto — (uses default welcome template)</SelectItem>
                    {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Reference-ID prefix</Label><Input value={typeForm.reference_id_prefix || ''} onChange={e => setTypeForm({...typeForm, reference_id_prefix: e.target.value.toUpperCase()})} placeholder="Defaults to code" /></div>
              <div><Label>Sort order</Label><Input type="number" value={typeForm.sort_order || 100} onChange={e => setTypeForm({...typeForm, sort_order: parseInt(e.target.value, 10) || 100})} /></div>
              <div className="md:col-span-2 flex items-center gap-2">
                <input id="type_active" type="checkbox" checked={typeForm.is_active !== false} onChange={e => setTypeForm({...typeForm, is_active: e.target.checked})} />
                <Label htmlFor="type_active" className="cursor-pointer">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTypeForm(null)} disabled={saving}><X className="h-4 w-4 mr-2" />Cancel</Button>
              <Button onClick={saveType} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-type-btn">
                <Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save type'}
              </Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
